import { use_auth } from "@/auth/useAuth";
import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { toast } from "vue3-toastify";
import type { filterSet } from "./filter";

export interface pagination<T> {
  data: T[];
  total_filter: number;
}

export interface fetchOptions<T> {
  beforeFetch?: (filter: filterSet<T> | undefined) => unknown;
  afterFetch?: (
    filter: filterSet<T> | undefined,
    response: AxiosResponse<pagination<T>> | undefined,
  ) => unknown;
  onError?: (err: unknown) => unknown;
}

export const fetch = async <T>(
  endpoint: string,
  filter: unknown,
  opts?: fetchOptions<T>,
) => {
  if (opts && opts.beforeFetch) {
    opts.beforeFetch(filter as filterSet<T>);
  }
  let response: AxiosResponse<pagination<T>> | undefined;
  try {
    response = await axios_instance.post(endpoint, filter);
  } catch (err) {
    if (opts && opts.onError) {
      opts.onError(err);
    }
  } finally {
    if (opts && opts.afterFetch) {
      opts.afterFetch(filter as filterSet<T>, response);
    }
  }
};

export const axios_instance = axios.create({
  withCredentials: true,
});

let refresh_in_flight: Promise<void> | null = null;

axios_instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const status = error.response.status;
      const config = error.config as (InternalAxiosRequestConfig & { _authRetry?: boolean }) | undefined;
      const url = config?.url ?? "";
      const authEndpoint = /\/view\/(login|refresh|logout)(?:$|\?)/.test(url);

      if (status === 401 && config && !config._authRetry && !authEndpoint) {
        config._authRetry = true;
        const refresh = () => axios_instance.post("/api/v1/view/refresh").then(() => undefined);
        // ponytail: without Web Locks, only this tab is serialized; cross-tab coordination requires Web Locks support.
        refresh_in_flight ??= ("locks" in navigator
          ? navigator.locks.request("rt-auth-refresh", refresh)
          : refresh()
        ).finally(() => { refresh_in_flight = null; });
        try {
          await refresh_in_flight;
        } catch (refreshError) {
          if (axios.isAxiosError(refreshError) && refreshError.response?.status === 401) {
            use_auth().clearAuth();
          }
          return Promise.reject(error);
        }
        return axios_instance.request(config);
      }
      if (status === 401 && config?._authRetry && !authEndpoint) {
        use_auth().clearAuth();
      }
      if (status === 403) {
        console.error("Forbidden (403) error detected.");
      } else if (status === 400 && error.response.data.message === "permission is disabled") {
        toast.error("Tính năng này đã bị vô hiệu hóa.");
      }
      return Promise.reject(error);
    }
    if (error.request) console.error("No response received:", error.request);
    else console.error("Error in request setup:", error.message);
    return Promise.reject(error);
  },
);
